import {RootState} from "@/store/Store";
import {createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {ManagerInterface} from "@/api/Users/Managers/Interfaces/ManagerInterface";
import {PartnerByManagerIdInterface} from "@/api/MassiveMailing/GetPartnersByManagerIdApiSlice";

type SelectedManagersInterface = ManagerInterface | null
export type PartnersInterface = PartnerByManagerIdInterface[]

function getInitialState() {
    return {
        selectedManagers: null as SelectedManagersInterface,
        innFilter: "",
        partners: [] as PartnersInterface,
        selectedPartners: [] as number[],
        isPartnersValidationError: false,
        isPartnersLoading: false,
        showResetModal: false,
        showClearModal: false,
    }
}

export const MassiveMailingSlice = createSlice({
    name: "MassiveMailingSlice",
    initialState: getInitialState(),
    reducers: {
        setSelectedManagers: (state, action: PayloadAction<SelectedManagersInterface>) => {
            state.selectedManagers = action.payload;
        },
        setInnFilter: (state, action: PayloadAction<string>) => {
            state.innFilter = action.payload;
        },
        setPartners: (state, action: PayloadAction<PartnersInterface>) => {
            state.partners = action.payload;
        },
        toggleSelectedPartner: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            const index = state.selectedPartners.indexOf(id);
            if (index === -1) {
                state.selectedPartners.push(id);
            } else {
                state.selectedPartners.splice(index, 1);
            }
        },
        toggleAllSelectedPartners: (state, action: PayloadAction<number[]>) => {
            const idsToToggle = action.payload;
            const allSelected = idsToToggle.every(id => state.selectedPartners.includes(id));

            if (allSelected) {
                state.selectedPartners = state.selectedPartners.filter(id => !idsToToggle.includes(id));
            } else {
                const uniqueIds = idsToToggle.filter(id => !state.selectedPartners.includes(id));
                state.selectedPartners.push(...uniqueIds);
            }
        },
        setIsPartnersValidationError: (state, action: PayloadAction<boolean>) => {
            state.isPartnersValidationError = action.payload;
        },
        removePartner: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.partners = state.partners.filter((p) => p.id !== id);
            state.selectedPartners = state.selectedPartners.filter(pId => pId !== id);
        },
        removeAllPartners: (state) => {
            state.partners = [];
            state.selectedPartners = [];
        },
        setIsPartnersLoading: (state, action: PayloadAction<boolean>) => {
            state.isPartnersLoading = action.payload;
        },
        setShowResetModal: (state, action: PayloadAction<boolean>) => {
            state.showResetModal = action.payload;
        },
        setShowClearModal: (state, action: PayloadAction<boolean>) => {
            state.showClearModal = action.payload;
        },
    },
});

export const {
    setSelectedManagers,
    setInnFilter,
    setPartners,
    toggleAllSelectedPartners,
    toggleSelectedPartner,
    setIsPartnersValidationError,
    removePartner,
    removeAllPartners,
    setIsPartnersLoading,
    setShowResetModal,
    setShowClearModal,
} = MassiveMailingSlice.actions;

const selectSelectedIds = (state: RootState) => state.massiveMailing.selectedPartners;

export const MassiveMailingSelectors = {
    selectedManagers: (state: RootState) => state.massiveMailing.selectedManagers,
    innFilter: (state: RootState) => state.massiveMailing.innFilter,
    partners: (state: RootState) => state.massiveMailing.partners,
    selectedPartners: (state: RootState) => state.massiveMailing.selectedPartners,
    selectionInfo: createSelector(
      [selectSelectedIds, (_state: RootState, currentPageIds: number[]) => currentPageIds],
      (selectedIds, currentPageIds) => {
          if (currentPageIds.length === 0) {
              return { isAllSelected: false, isIndeterminate: false };
          }

          const selectedOnPage = currentPageIds.filter(id => selectedIds.includes(id));
          const isAllSelected = selectedOnPage.length === currentPageIds.length;
          const isIndeterminate = selectedOnPage.length > 0 && !isAllSelected;

          return { isAllSelected, isIndeterminate };
      }
    ),
    isPartnerSelected: (state: RootState, partnerId: number) => state.massiveMailing.selectedPartners.includes(partnerId),
    isPartnersValidationError: (state: RootState) => state.massiveMailing.isPartnersValidationError,
    isPartnersLoading: (state: RootState) => state.massiveMailing.isPartnersLoading,
    showResetModal: (state: RootState) => state.massiveMailing.showResetModal,
    showClearModal: (state: RootState) => state.massiveMailing.showClearModal,
}

export const MassiveMailingReducer = MassiveMailingSlice.reducer;
